// Generated by GitHub Copilot
import { useState } from "react";
import { buildApiUrl } from "../utils/api";
import {
  VISITOR_MODES,
  VISITOR_MODE_STORAGE_KEY,
  buildLocalPortfolioReply,
  buildModeSystemPrompt,
  getInitialAssistantMessage,
  normalizeVisitorMode,
} from "../utils/chatRuntime";

/**
 * Read and normalize the persisted visitor mode from session storage.
 *
 * @returns {"hiring" | "browsing" | ""} Normalized visitor mode.
 */
function loadStoredVisitorMode() {
  if (typeof window === "undefined") {
    return "";
  }

  return normalizeVisitorMode(window.sessionStorage.getItem(VISITOR_MODE_STORAGE_KEY));
}

function Chatbot({ config, apiBaseUrl }) {
  const chatApiUrl = buildApiUrl("/chat", apiBaseUrl);
  const [open, setOpen] = useState(false);
  const [visitorMode, setVisitorMode] = useState(() => loadStoredVisitorMode());
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content: getInitialAssistantMessage(config?.welcomeMessage, loadStoredVisitorMode()),
    },
  ]);

  /**
   * Persist selected visitor mode and append a context acknowledgement.
   *
   * @param {"hiring" | "browsing"} nextMode Visitor mode selected by the user.
   */
  function selectVisitorMode(nextMode) {
    const normalizedMode = normalizeVisitorMode(nextMode);
    if (!normalizedMode) {
      return;
    }

    setVisitorMode(normalizedMode);

    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(VISITOR_MODE_STORAGE_KEY, normalizedMode);
    }

    const acknowledgement =
      normalizedMode === VISITOR_MODES.HIRING
        ? "Hiring mode enabled. I will answer with concise recruiter-focused evidence and contact options."
        : "Browsing mode enabled. I will answer using strict portfolio facts from the page and synced profile data.";

    setMessages((prev) => {
      if (prev[prev.length - 1]?.content === acknowledgement) {
        return prev;
      }

      return [...prev, { role: "assistant", content: acknowledgement }];
    });
  }

  /**
   * Send a user query through the backend proxy with mode-aware prompting.
   */
  async function sendMessage() {
    if (loading) {
      return;
    }

    if (!visitorMode) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Please select Hiring or Browsing first so I can tailor your response correctly.",
        },
      ]);
      return;
    }

    if (!question.trim()) {
      return;
    }

    const userMessage = { role: "user", content: question.trim() };
    const history = messages
      .slice(-4)
      .filter((entry) => entry.role === "user" || entry.role === "assistant")
      .map((entry) => ({
        role: entry.role,
        content: String(entry.content || "").slice(0, 500),
      }));

    setMessages((prev) => [...prev, userMessage]);
    setQuestion("");
    setLoading(true);

    const effectiveSystemPrompt = buildModeSystemPrompt(config?.systemPrompt, visitorMode);

    try {
      const response = await fetch(chatApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userMessage.content,
          systemPrompt: effectiveSystemPrompt,
          history,
          visitorMode,
        }),
      });

      if (!response.ok) {
        throw new Error(`Chat request failed: ${response.status}`);
      }

      const payload = await response.json();
      const content = payload?.reply || payload?.content;
      if (!content) {
        throw new Error("Chat payload missing content");
      }

      setMessages((prev) => [...prev, { role: "assistant", content }]);
    } catch (error) {
      const fallbackReply = buildLocalPortfolioReply(userMessage.content, visitorMode);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: fallbackReply,
        },
      ]);
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {open && (
        <div className="glass-card-strong mb-3 w-[320px] rounded-2xl p-4 md:w-[360px]">
          <h3 className="mb-3 text-sm font-bold text-[rgba(var(--accent-rgb),0.95)]">
            {config?.title || "Dragon Assistant"}
          </h3>
          <div className="glass-card-soft mb-3 rounded-xl p-2">
            <p className="text-xs font-semibold text-[var(--text-secondary)]">Conversation mode</p>
            <div
              role="radiogroup"
              aria-label="Select visitor context"
              className="mt-2 flex items-center gap-2"
            >
              <button
                type="button"
                role="radio"
                aria-checked={visitorMode === VISITOR_MODES.HIRING}
                onClick={() => selectVisitorMode(VISITOR_MODES.HIRING)}
                className={`${visitorMode === VISITOR_MODES.HIRING ? "glass-button-primary" : "glass-button-secondary"} rounded-md px-3 py-1.5 text-xs font-semibold`}
              >
                Hiring
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={visitorMode === VISITOR_MODES.BROWSING}
                onClick={() => selectVisitorMode(VISITOR_MODES.BROWSING)}
                className={`${visitorMode === VISITOR_MODES.BROWSING ? "glass-button-primary" : "glass-button-secondary"} rounded-md px-3 py-1.5 text-xs font-semibold`}
              >
                Browsing
              </button>
            </div>
            <p className="mt-2 text-[11px] text-[var(--text-secondary)]">
              {visitorMode === VISITOR_MODES.HIRING
                ? "Mode active: Hiring (concise fit, impact evidence, and contact details)."
                : visitorMode === VISITOR_MODES.BROWSING
                  ? "Mode active: Browsing (strict portfolio facts only)."
                  : "Select a mode first to start chatting."}
            </p>
          </div>
          <div className="mb-3 max-h-64 space-y-2 overflow-y-auto pr-1">
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`rounded-lg p-2 text-sm ${message.role === "user" ? "bg-[rgba(var(--accent-rgb),0.18)] text-[var(--text-primary)]" : "glass-card-soft text-[var(--text-primary)]"
                  }`}
              >
                {message.content}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              className="glass-input w-full px-3 py-2 text-sm"
              value={question}
              disabled={loading || !visitorMode}
              onChange={(event) => setQuestion(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  sendMessage();
                }
              }}
              placeholder={
                visitorMode
                  ? config?.placeholder || "Ask about projects..."
                  : "Choose Hiring or Browsing to start chat"
              }
              aria-label="Chat question input"
            />
            <button
              type="button"
              onClick={sendMessage}
              disabled={loading || !visitorMode}
              className="glass-button-primary rounded-lg px-3 py-2 text-sm font-semibold disabled:opacity-60"
            >
              Send
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="glass-button-primary rounded-full px-5 py-3 text-sm font-bold"
      >
        {open ? "Close Chat" : "Open AI Chat"}
      </button>
    </div>
  );
}

export default Chatbot;
